import type { TooltipProps } from '@mui/material';
import { inputClasses } from '@mui/material';
import { VyTooltip } from '../../../../../../components/new-design/VyTooltip';

import { DEFAULT_CURRENCY, PRICING_MODELS, type FixedProductInput } from '@vy/types';
import { isNotEmptyObject, isNotNullOrUndefined, withDefault } from '@vy/utils';
import { get, isNumber } from 'lodash';
import type { ComponentProps } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { FieldPath } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { VayuTextField } from '../../../../../../components/design/vayu-theme-components/VayuTextField';
import { useFormErrorNotificationContext } from '../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useIsOverflowed } from '../../../../../../hooks/use-is-overflowed';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { useDebounce } from '../../../../../../hooks/use-debounce';
import { userMessage } from '../../../../../../utils/vy-toasts';
import { usePreviousValue } from '../../../../../../hooks/use-previous-value';
import { formatPrice } from '@vy/services';

type EditCellProps = {
  fixedProduct: FixedProductInput;
  field: FieldPath<FixedProductInput>;
  tooltipPlacement?: TooltipProps['placement'];
  valueAsNumber?: boolean;
  isPrice?: boolean;
} & ComponentProps<typeof VayuTextField>;

export const ProductEditCell: React.FC<EditCellProps> = ({
  fixedProduct,
  field,
  tooltipPlacement = 'top',
  valueAsNumber = false,
  isPrice = false,
  ...props
}) => {
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const {
    register,
    getValues,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<PlanInputForm, NewPlanForm>();
  const products = withDefault(getValues('fixedProducts'), []);
  const productIndex = products.findIndex((product) => product.id === fixedProduct.id);

  const registerField = register(`fixedProducts.${productIndex}.${field}`, {
    valueAsNumber,
  });
  registerErrorField(registerField.name, CREATE_PLAN_TABS.serviceFees);

  const scheduledInstallments = register(
    `fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.scheduledInstallments`,
  );
  const isCommercialTerms =
    fixedProduct.variants.at(0)?.pricing.pricingModel === PRICING_MODELS.CommercialTerms;

  const priceConfiguration = watch(`fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration`);

  const hasPrice = isNotNullOrUndefined(priceConfiguration) && 'price' in priceConfiguration;

  const fromProductPrice = isCommercialTerms && hasPrice ? priceConfiguration.price : undefined;

  const debouncedPrice = useDebounce(fromProductPrice, 1000);
  const prevPrice = usePreviousValue(debouncedPrice);

  const discountAmount = watch(
    `fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.discountConfiguration.amountToDeduct`,
  );
  const discountName = watch(
    `fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.discountConfiguration.name`,
  );

  useEffect(() => {
    if (!isCommercialTerms) {
      return;
    }

    if (prevPrice === debouncedPrice || (!isNumber(debouncedPrice) && !isNumber(prevPrice))) {
      return;
    }

    const productInstallments = getValues(scheduledInstallments.name);

    if (
      isNotNullOrUndefined(productInstallments) &&
      isNotNullOrUndefined(productInstallments.installments) &&
      productInstallments.installments.length > 1
    ) {
      userMessage('warning', 'Installments reset due to price change. Please reconfigure.');
    }
  }, [debouncedPrice, prevPrice, isCommercialTerms, scheduledInstallments.name, setValue]);

  useEffect(() => {
    if (
      isNotNullOrUndefined(priceConfiguration) &&
      'discountConfiguration' in priceConfiguration &&
      isNotEmptyObject(priceConfiguration.discountConfiguration)
    ) {
      const registerDiscountAmountField = register(
        `fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.discountConfiguration.amountToDeduct`,
        {
          valueAsNumber,
        },
      );

      const registerDiscountNameField = register(
        `fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.discountConfiguration.name`,
      );

      registerErrorField(registerDiscountAmountField.name, CREATE_PLAN_TABS.serviceFees);
      registerErrorField(registerDiscountNameField.name, CREATE_PLAN_TABS.serviceFees);
    }
  }, [
    discountAmount,
    discountName,
    products,
    priceConfiguration,
    getValues,
    productIndex,
    register,
    valueAsNumber,
    registerErrorField,
  ]);

  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const editCellRef = useRef<HTMLInputElement>(null);
  const isInputOverflowed = useIsOverflowed(editCellRef);
  const inputValue = getValues(`fixedProducts.${productIndex}.${field}`) || 0;
  const formattedValue = isPrice ? formatPrice(inputValue, DEFAULT_CURRENCY, 2, 2, false) : inputValue;

  return (
        <VyTooltip
      tooltipContent={getValues(`fixedProducts.${productIndex}.${field}`)?.toString() ?? ''}
      placement={tooltipPlacement}
      arrow
      open={
        !isEditing &&
        isHovering &&
        isInputOverflowed &&
        !Number.isNaN(getValues(`fixedProducts.${productIndex}.${field}`))
      }
    >
      <VayuTextField
        {...registerField}
        onKeyDown={(event) => event.stopPropagation()}
        value={isEditing ? inputValue : formattedValue}
        sx={{
          ...props.sx,
          [`& .${inputClasses.root}`]: {
            border: 'none',
            ['&.Mui-focused']: {
              border: '1px solid #E1E6E8',
              padding: '4px 16px !important',
            },
          },
        }}
        inputRef={editCellRef}
        {...props}
        type={isEditing ? 'number' : 'text'}
        onFocus={(event) => {
          setIsEditing(true);
          props.onFocus?.(event);
        }}
        onBlur={(event) => {
          setIsEditing(false);
          props.onBlur?.(event);
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        error={isNotNullOrUndefined(get(errors, registerField.name))}
      />
    </VyTooltip>
  );
};
